import axios from 'axios';
import Promise$1 from 'bluebird';
import { ApiClientError } from '../errors/ApiClientError.js';
import { RequestContextError } from '../errors/RequestContextError.js';
import { Tenant } from '../models/Tenant/Tenant.js';

/**
 * This file performs post, get, put ... operations
 * We cannot use Record<string, unknown> type for params or paramsSerializer
 * As we actually don't know what we are sending, can be a Warehouse create payload
 * or Shipment.
 */
Promise$1.config({
  cancellation: true,
  warnings: false
});
let PacklinkApiRequestMethod = /*#__PURE__*/function (PacklinkApiRequestMethod) {
  PacklinkApiRequestMethod["DELETE"] = "DELETE";
  PacklinkApiRequestMethod["GET"] = "GET";
  PacklinkApiRequestMethod["POST"] = "POST";
  PacklinkApiRequestMethod["PUT"] = "PUT";
  PacklinkApiRequestMethod["PATCH"] = "PATCH";
  return PacklinkApiRequestMethod;
}({});
class ApiClient {
  constructor(config, application, requestContext, packlinkAuth, tenant) {
    this.config = config;
    this.application = application;
    this.requestContext = requestContext;
    this.packlinkAuth = packlinkAuth;
    this.tenant = tenant;
    this.client = this.createClient(this.config.url, this.packlinkAuth, this.tenant ? this.mapTenantToRequestContext(this.tenant) : this.requestContext);
    this.tenant && this.setTenant(this.tenant);
  }
  get(endpoint, params, headers, paramsSerializer) {
    return this.request(PacklinkApiRequestMethod.GET, endpoint, params, undefined, headers, paramsSerializer);
  }
  delete(endpoint, body, params, headers) {
    return this.request(PacklinkApiRequestMethod.DELETE, endpoint, params, body, headers);
  }
  post(endpoint, body, params, headers, responseType, paramsSerializer) {
    return this.request(PacklinkApiRequestMethod.POST, endpoint, params, body, headers, paramsSerializer, responseType);
  }
  put(endpoint, body, params, headers) {
    return this.request(PacklinkApiRequestMethod.PUT, endpoint, params, body, headers);
  }
  patch(endpoint, body, params, headers) {
    return this.request(PacklinkApiRequestMethod.PATCH, endpoint, params, body, headers);
  }
  getAuthorization() {
    return this.packlinkAuth;
  }
  getRequestContext() {
    return this.requestContext;
  }
  getApplication() {
    return this.application;
  }
  getTenant() {
    return this.tenant;
  }
  setAuthorization(packlinkAuth) {
    this.packlinkAuth = packlinkAuth;
    if (this.packlinkAuth) {
      this.client.defaults.headers.common.Authorization = this.getAuthHeader();
      delete this.client.defaults.headers.common['x-request-context'];
    } else {
      delete this.client.defaults.headers.common.Authorization;
    }
  }
  setTenant(tenant) {
    const requestContext = this.mapTenantToRequestContext(tenant);
    this.tenant = tenant;
    // Set always request context defined by tenant because tenant is the source of truth
    this.setRequestContext(requestContext);
    // Tenant propagation to backend
    this.client.defaults.headers.common['X-Packlink-Tenant-ID'] = this.tenant.name;
    this.client.defaults.headers.common['X-Packlink-Application-ID'] = this.application;
  }
  setRequestContext(requestContext) {
    this.requestContext = requestContext;
    if (!this.packlinkAuth) {
      this.client.defaults.headers.common['x-request-context'] = JSON.stringify(requestContext);
    }

    // No remove this check because this could cause a infinite loop buildTenantFromContext -> setTenant -> setRequestContext -> buildTenantFromContext ...
    // Our source of truth is the tenant and if we decide to modify the request context we need to set tenant if is not defined
    if (!this.tenant) {
      this.buildTenantFromContext();
    }
  }
  withRequestContext() {
    if (!this.requestContext) {
      throw new RequestContextError();
    }
    const {
      platform,
      platform_country
    } = this.requestContext;
    return {
      platform,
      platform_country
    };
  }
  buildTenantFromContext() {
    if (!this.tenant && this.requestContext !== undefined) {
      this.tenant = Tenant.mapPlatAndPlatCountryToTenant(this.requestContext.platform, this.requestContext.platform_country);
      this.setTenant(this.tenant);
    }
  }
  request(method, url, params, data) {
    let headers = arguments.length > 4 && arguments[4] !== undefined ? arguments[4] : {};
    let paramsSerializer = arguments.length > 5 ? arguments[5] : undefined;
    let responseType = arguments.length > 6 ? arguments[6] : undefined;
    const cancelTokenSource = axios.CancelToken.source();
    const requestConfig = {
      cancelToken: cancelTokenSource.token,
      data,
      headers,
      method,
      params,
      paramsSerializer,
      url,
      responseType
    };
    return new Promise$1((resolve, reject, onCancel) => {
      this.client.request(requestConfig).then(resolve).catch(reject);
      onCancel(() => {
        cancelTokenSource.cancel();
      });
    });
  }
  mapTenantToRequestContext(tenant) {
    const tenantRequestContext = {
      platform: tenant.mapToPlatform(),
      platform_country: tenant.mapToPlatformCountry()
    };
    return {
      ...this.requestContext,
      ...tenantRequestContext
    };
  }
  getAuthHeader() {
    if (this.packlinkAuth) {
      return this.packlinkAuth.token;
    }
    return '';
  }
  createClient(apiUrl, auth, requestContext) {
    const baseURL = apiUrl.charAt(apiUrl.length - 1) === '/' ? apiUrl : `${apiUrl}/`;
    const client = axios.create({
      baseURL
    });
    client.defaults.headers.common['Content-Type'] = 'application/json';
    if (auth) {
      client.defaults.headers.common.Authorization = this.getAuthHeader();
    } else if (requestContext) {
      client.defaults.headers.common['x-request-context'] = JSON.stringify(requestContext);
    }
    client.interceptors.request.use(config => config, error => {
      const apiClientError = new ApiClientError(error);
      apiClientError.log();
      return Promise$1.reject(apiClientError);
    });
    client.interceptors.response.use(response => response, error => {
      const apiClientError = new ApiClientError(error);
      apiClientError.log();
      return Promise$1.reject(apiClientError);
    });
    return client;
  }
}

export { ApiClient, PacklinkApiRequestMethod };
//# sourceMappingURL=ApiClient.js.map
